import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { loadSession, type Session } from "./api";
import { AppContext, paintTheme, type AppActions } from "./appContext";
import { ActivityPage, MfaPage, PasswordPage, PreferencesPage, ProfilePage, SecurityPage, SessionsPage as AccountSessionsPage } from "./account/AccountPages";
import { HubExplorePage, HubForbidden, HubIssuesPage, HubPerformancePage, HubReleasesPage, HubSummaryPage } from "./hub/HubPages";
import { ChangePasswordPage } from "./auth/ChangePasswordPage";
import { LoginPage } from "./auth/LoginPage";
import { OrganizationNewPage } from "./orgs/OrganizationNewPage";
import { OrganizationPage } from "./orgs/OrganizationPage";
import { OrganizationsPage } from "./orgs/OrganizationsPage";
import { UserNewPage } from "./orgs/UserNewPage";
import { InvitationsPage, OwnerPage, UsersPage } from "./orgs/PeoplePage";
import { UserPage } from "./orgs/UserPage";
import { PoliciesPage, RecoverPage, SessionsPage } from "./orgs/SecurityPages";
import { AuditPage, DiagnosticPage, OrgUsagePage } from "./orgs/SupportPages";
import { EmployeeNewPage, EmployeePage, EmployeesPage } from "./orgs/EmployeesPages";
import { BasePage, KnowledgePage, SourceNewPage, SourcePage, TermsPage } from "./orgs/KnowledgePages";
import { ApprovalPage, ApprovalPoliciesPage, ApprovalsPage } from "./orgs/ApprovalsPages";
import { normalizeEmployeeTab } from "./orgs/employees";
import { ChannelsPage, InboxesPage, RulesPage } from "./orgs/MessagingPages";
import { ConversationPage, ConversationsPage } from "./orgs/ConversationsPages";
import { SimChatPage, SimulatorPage } from "./orgs/SimulatorPages";
import { normalizeConversationTab } from "./orgs/messaging";
import { HomePage } from "./home/HomePage";
import { ErrorRoute, FrameBoundary, OrgNotFound, PageNotFound, ScreenBoundary } from "./pages/StatePages";
import { PiecesPage } from "./pages/PiecesPage";
import { AdminShell } from "./shell/AdminShell";
import { scrollPageTop } from "./ui/scroll";
import { ConnectionRedirect, LegacyAiRedirect } from "./ai/redirects";
import { CustomProviderPage } from "./ai/CustomProviderPage";
import { ExecutionsPage } from "./ai/ExecutionsPage";
import { ManualModelPage } from "./ai/ManualModelPage";
import { ModelPage } from "./ai/ModelPage";
import { ModelsPage } from "./ai/ModelsPage";
import { OverviewPage } from "./ai/OverviewPage";
import { ProviderPage } from "./ai/ProviderPage";
import { ProvidersPage } from "./ai/ProvidersPage";
import { RoutesPage } from "./ai/RoutesPage";
import { UsageDetailPage } from "./ai/UsageDetailPage";
import { UsagePage } from "./ai/UsagePage";
import { TextsContext, loadTexts, localeFromCookie, missingText, type Label, type Locale, type Texts } from "./texts";

/**
 * Como la navegación mejorada de Blazor: un clic en un enlace interno cambia de pantalla sin recargar. Los enlaces a otro
 * origen, a otra pestaña o con tecla modificadora siguen siendo enlaces normales.
 */
function useSpaLinks() {
  const navigate = useNavigate();
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor = (event.target as Element | null)?.closest?.("a[href]");
      if (!(anchor instanceof HTMLAnchorElement) || anchor.target || anchor.hasAttribute("download")) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || url.hash) return;
      event.preventDefault();
      navigate(url.pathname + url.search);
      scrollPageTop();
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);
}

/** Al cambiar de pantalla el foco va al título (como FocusOnNavigate del Platform Admin actual). */
function useFocusOnNavigate() {
  const location = useLocation();
  useEffect(() => {
    requestAnimationFrame(() => document.querySelector<HTMLElement>("main h1")?.focus({ preventScroll: true }));
  }, [location.pathname]);
}

/** Pantallas con sesión: sin sesión, al ingreso volviendo después a la misma dirección. */
function Private({ session, children }: { session: Session; children: ReactNode }) {
  const location = useLocation();
  if (!session.authenticated)
    return <Navigate replace to={`/login?ReturnUrl=${encodeURIComponent(location.pathname + location.search)}`} />;
  // Si una pantalla falla al dibujarse, el marco sigue y la pantalla dice «Algo salió mal» con su referencia (C40).
  return (
    <FrameBoundary>
      <AdminShell session={session}>
        <ScreenBoundary resetKey={location.pathname + location.search}>{children}</ScreenBoundary>
      </AdminShell>
    </FrameBoundary>
  );
}

/** El ingreso con sesión vigente va directo a la pantalla pedida (como Login.razor). */
function Public({ session, children }: { session: Session; children: ReactNode }) {
  const [params] = useSearchParams();
  if (!session.authenticated) return <>{children}</>;
  const back = params.get("ReturnUrl");
  return <Navigate replace to={back && back.startsWith("/") && !back.startsWith("//") ? back : "/"} />;
}

/** Ficha del proveedor por su código (/Ai/Providers/{providerCode}). */
function ProviderRoute() {
  const { code } = useParams();
  return <ProviderPage key={code} code={code ?? ""} />;
}

const positive = (value: string | null | undefined) => {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
};

/** Resumen de la empresa por /Organizations/Detail?id=; la forma vieja /Organizations/Detail/{id} redirige (§4). */
function DetailRoute() {
  const { id } = useParams();
  const [params] = useSearchParams();
  if (id) return <Navigate replace to={positive(id) ? `/Organizations/Detail?id=${positive(id)}` : "/not-found"} />;
  const value = positive(params.get("id"));
  return value ? <OrganizationPage key={value} id={value} /> : <OrgNotFound />;
}

/** Pantalla de una empresa (?id=): sin un id válido, «No encontramos esta empresa» (C40). */
function OrgRoute({ render }: { render: (id: number) => ReactNode }) {
  const [params] = useSearchParams();
  const value = positive(params.get("id"));
  return value ? <>{render(value)}</> : <OrgNotFound />;
}

/** Ficha del usuario (?id=&userId=&tab=); sin persona, la lista de Usuarios de la empresa. */
function UserRoute() {
  const [params] = useSearchParams();
  const org = positive(params.get("id"));
  const user = positive(params.get("userId"));
  if (!org) return <OrgNotFound />;
  if (!user) return <Navigate replace to={`/Organizations/Usuarios?id=${org}`} />;
  return <UserPage key={`${org}-${user}`} orgId={org} userId={user} />;
}

/**
 * Rutas Blazor que se funden (§4): Módulos → Resumen; AbrirComoUsuario → Resumen con la ventana abierta (por estado:
 * una ventana abierta por la dirección pierde el foco); RecuperacionPropietario → Propietario; AgregarUsuarioCompletado
 * → Agregar usuario; EventosTecnicos → Diagnóstico; Trazas → Uso de IA (con su período y modo, si los trae).
 */
function OrgRedirect({ to, open, keep = [] }: { to: string; open?: string; keep?: string[] }) {
  const [params] = useSearchParams();
  const org = positive(params.get("id"));
  if (!org) return <Navigate replace to="/Organizations/Index" />;
  const user = positive(params.get("userId"));
  let target = to.replace("{id}", String(org)).replace("{userId}", String(user ?? ""));
  for (const key of keep) {
    const value = params.get(key);
    if (value) target += `&${key}=${encodeURIComponent(value)}`;
  }
  return <Navigate replace to={target} state={open ? { open } : undefined} />;
}

/**
 * Sesiones (§4): con `userId` (enlace viejo a las sesiones de una persona) va a su ficha en Seguridad; sin él, son las
 * sesiones de la empresa (R4).
 */
function SessionsRoute() {
  const [params] = useSearchParams();
  const org = positive(params.get("id"));
  const user = positive(params.get("userId"));
  if (!org) return <OrgNotFound />;
  if (user) return <Navigate replace to={`/Organizations/Usuario?id=${org}&userId=${user}&tab=seguridad`} />;
  return <SessionsPage key={org} orgId={org} />;
}

/** Valor de la dirección por su nombre nuevo o por el del Blazor (employee, base, source, approval). */
function recordId(params: URLSearchParams, name: string, legacy: string) {
  return positive(params.get(name)) ?? positive(params.get(legacy));
}

/**
 * Ficha del empleado IA (?id=&employeeId=&tab=); sin empleado, Nuevo empleado IA (§4). Capacidades y
 * EmpleadoConocimiento del Blazor (con `employee`) llegan a su pestaña.
 */
function EmployeeRoute({ tab }: { tab?: "capacidades" | "conocimiento" }) {
  const [params] = useSearchParams();
  const org = positive(params.get("id"));
  const employee = recordId(params, "employeeId", "employee");
  if (!org) return <OrgNotFound />;
  if (tab)
    return (
      <Navigate
        replace
        to={
          employee
            ? `/Organizations/Empleado?id=${org}&employeeId=${employee}&tab=${tab}`
            : `/Organizations/Empleados?id=${org}`
        }
      />
    );
  if (!employee) return <EmployeeNewPage key={org} orgId={org} />;
  const current = normalizeEmployeeTab(params.get("tab"));
  return <EmployeePage key={`${org}-${employee}`} orgId={org} employeeId={employee} tab={current} />;
}

/** Ficha de la base (?id=&baseId=&tab=); ConocimientoBase del Blazor (alta o edición) lleva a la ficha o a la lista. */
function BaseRoute() {
  const [params] = useSearchParams();
  const org = positive(params.get("id"));
  const base = recordId(params, "baseId", "base");
  if (!org) return <OrgNotFound />;
  if (!base) return <Navigate replace to={`/Organizations/Conocimiento?id=${org}`} />;
  const tab = params.get("tab") === "clasificacion" ? "clasificacion" : "fuentes";
  return <BasePage key={`${org}-${base}`} orgId={org} baseId={base} tab={tab} />;
}

/** Ficha de la fuente (?id=&sourceId=&tab=); sin fuente, Conocimiento. */
function SourceRoute() {
  const [params] = useSearchParams();
  const org = positive(params.get("id"));
  const source = recordId(params, "sourceId", "source");
  if (!org) return <OrgNotFound />;
  if (!source) return <Navigate replace to={`/Organizations/Conocimiento?id=${org}`} />;
  const value = params.get("tab");
  const tab = value === "versiones" || value === "clasificacion" ? value : "contenido";
  return <SourcePage key={`${org}-${source}`} orgId={org} sourceId={source} tab={tab} />;
}

/** Nueva fuente (?id=&baseId=); la edición del Blazor (?source=) es la ventana de la ficha de la fuente. */
function SourceNewRoute() {
  const [params] = useSearchParams();
  const org = positive(params.get("id"));
  const base = recordId(params, "baseId", "base");
  const source = recordId(params, "sourceId", "source");
  if (!org) return <OrgNotFound />;
  if (source)
    return <Navigate replace to={`/Organizations/ConocimientoFuente?id=${org}&sourceId=${source}&tab=contenido`} />;
  if (!base) return <Navigate replace to={`/Organizations/Conocimiento?id=${org}`} />;
  return <SourceNewPage key={`${org}-${base}`} orgId={org} baseId={base} />;
}

/** Aprobaciones (?id=&status=&page=); la pestaña de políticas del Blazor (?tab=politicas) tiene su propia ruta. */
function ApprovalsRoute() {
  const [params] = useSearchParams();
  const org = positive(params.get("id"));
  if (!org) return <OrgNotFound />;
  if (params.get("tab") === "politicas")
    return <Navigate replace to={`/Organizations/Aprobaciones/Politicas?id=${org}`} />;
  return <ApprovalsPage key={org} orgId={org} />;
}

/** Solicitud (?id=&approvalId=, o ?approval= del Blazor); sin solicitud, la cola. */
function ApprovalRoute() {
  const [params] = useSearchParams();
  const org = positive(params.get("id"));
  const approval = recordId(params, "approvalId", "approval");
  if (!org) return <OrgNotFound />;
  if (!approval) return <Navigate replace to={`/Organizations/Aprobaciones?id=${org}`} />;
  return <ApprovalPage key={`${org}-${approval}`} orgId={org} approvalId={approval} />;
}

/** Conversaciones internas (?id=&search=&status=&page=); la búsqueda del Blazor (?q=) llega como `search`. */
function ConversationsRoute() {
  const [params] = useSearchParams();
  const org = positive(params.get("id"));
  if (!org) return <OrgNotFound />;
  const legacy = params.get("q");
  if (legacy !== null) {
    const next = new URLSearchParams(params);
    next.delete("q");
    if (legacy.trim()) next.set("search", legacy.trim());
    if (next.get("status") === "ALL") next.delete("status");
    return <Navigate replace to={`/Organizations/Conversaciones?${next}`} />;
  }
  return <ConversationsPage key={org} orgId={org} />;
}

/** Ficha de una conversación interna (?id=&caseId=&tab=, o ?case= del Blazor); sin conversación, la lista. */
function ConversationRoute() {
  const [params] = useSearchParams();
  const org = positive(params.get("id"));
  const caseId = recordId(params, "caseId", "case");
  if (!org) return <OrgNotFound />;
  if (!caseId) return <Navigate replace to={`/Organizations/Conversaciones?id=${org}`} />;
  if (!params.get("caseId"))
    return (
      <Navigate replace to={`/Organizations/Conversacion?id=${org}&caseId=${caseId}&tab=${normalizeConversationTab(params.get("tab"))}`} />
    );
  const tab = normalizeConversationTab(params.get("tab"));
  return <ConversationPage key={`${org}-${caseId}`} orgId={org} caseId={caseId} tab={tab} />;
}

/**
 * Conversación simulada (?id=&contactId=&sessionId=); la página Cliente del Blazor (/Organizations/Client) llega aquí con
 * su cliente (§4). Sin cliente, el Simulador.
 */
function SimChatRoute() {
  const [params] = useSearchParams();
  const org = positive(params.get("id"));
  const contact = positive(params.get("contactId"));
  const session = positive(params.get("sessionId"));
  if (!org) return <OrgNotFound />;
  if (!contact) return <Navigate replace to={`/Organizations/Simulator?id=${org}`} />;
  return <SimChatPage key={`${org}-${contact}-${session ?? ""}`} orgId={org} contactId={contact} sessionId={session} />;
}

/**
 * Pantalla con un id en la ruta (/Detail/{id}) o en la consulta (?id=, ?connectionId=); sin un id válido vuelve a su
 * lista.
 */
function IdRoute({
  query = "id",
  fallback,
  render,
}: {
  query?: string;
  fallback: string;
  render: (id: number) => ReactNode;
}) {
  const { id } = useParams();
  const [params] = useSearchParams();
  const value = Number(id ?? params.get(query));
  return Number.isSafeInteger(value) && value > 0 ? <>{render(value)}</> : <Navigate replace to={fallback} />;
}

function Screens({ session }: { session: Session }) {
  useSpaLinks();
  useFocusOnNavigate();
  const inside = (screen: ReactNode) => <Private session={session}>{screen}</Private>;
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Public session={session}>
            <LoginPage />
          </Public>
        }
      />
      <Route path="/cambiarpassword" element={<ChangePasswordPage />} />
      {/* D2, D18: «/» es Inicio. */}
      <Route path="/" element={inside(<HomePage session={session} />)} />
      <Route path="/not-found" element={inside(<PageNotFound />)} />
      <Route path="/Error" element={inside(<ErrorRoute />)} />
      {/* Banco de prueba de las piezas compartidas C40, C50–C55 y C28 (sin enlace en el menú). */}
      <Route path="/_piezas" element={inside(<PiecesPage />)} />
      {/* Error Hub (R7, §4): sin su permiso, C40 «No tienes acceso a Error Hub» dentro del marco (el servidor igual lo
          exige en cada lectura). */}
      {(
        [
          ["/ErrorHub", <HubSummaryPage />, "summary"],
          ["/ErrorHub/Issues", <HubIssuesPage />, "issues"],
          ["/ErrorHub/Explore", <HubExplorePage />, "explore"],
          ["/ErrorHub/Performance", <HubPerformancePage />, "performance"],
          ["/ErrorHub/Releases", <HubReleasesPage />, "releases"],
        ] as const
      ).map(([path, screen, section]) => (
        <Route key={path} path={path} element={inside(session.canErrorHub ? screen : <HubForbidden section={section} />)} />
      ))}
      {/* Mi cuenta (R7, §4): se llega desde el menú del avatar (D2). */}
      <Route path="/Account" element={<Navigate replace to="/Account/Profile" />} />
      <Route path="/Account/Profile" element={inside(<ProfilePage />)} />
      <Route path="/Account/Preferences" element={inside(<PreferencesPage />)} />
      <Route path="/Account/Security" element={inside(<SecurityPage />)} />
      <Route path="/Account/Security/Password" element={inside(<PasswordPage />)} />
      <Route path="/Account/Security/Mfa" element={inside(<MfaPage />)} />
      <Route path="/Account/Security/Sessions" element={inside(<AccountSessionsPage />)} />
      <Route path="/Account/Security/Activity" element={inside(<ActivityPage />)} />
      <Route path="/Organizations" element={<Navigate replace to="/Organizations/Index" />} />
      <Route path="/Organizations/Index" element={inside(<OrganizationsPage />)} />
      <Route path="/Organizations/Crear" element={inside(<OrganizationNewPage />)} />
      <Route path="/Organizations/Detail" element={inside(<DetailRoute />)} />
      <Route path="/Organizations/Detail/:id" element={inside(<DetailRoute />)} />
      {/* Empresas y Personas (R3, especificación §4). */}
      <Route
        path="/Organizations/AgregarUsuario"
        element={inside(<OrgRoute render={(id) => <UserNewPage key={id} id={id} />} />)}
      />
      <Route
        path="/Organizations/Usuarios"
        element={inside(<OrgRoute render={(id) => <UsersPage key={id} orgId={id} />} />)}
      />
      <Route
        path="/Organizations/Invitaciones"
        element={inside(<OrgRoute render={(id) => <InvitationsPage key={id} orgId={id} />} />)}
      />
      <Route
        path="/Organizations/Propietario"
        element={inside(<OrgRoute render={(id) => <OwnerPage key={id} orgId={id} />} />)}
      />
      <Route path="/Organizations/Usuario" element={inside(<UserRoute />)} />
      <Route path="/Organizations/Modulos" element={<OrgRedirect to="/Organizations/Detail?id={id}" />} />
      <Route
        path="/Organizations/AbrirComoUsuario"
        element={<OrgRedirect to="/Organizations/Detail?id={id}" open="abrir" />}
      />
      <Route
        path="/Organizations/RecuperacionPropietario"
        element={<OrgRedirect to="/Organizations/Propietario?id={id}" />}
      />
      <Route
        path="/Organizations/AgregarUsuarioCompletado"
        element={<OrgRedirect to="/Organizations/AgregarUsuario?id={id}" />}
      />
      {/* Seguridad y acceso · Uso y soporte (R4, especificación §4). */}
      <Route path="/Organizations/Sesiones" element={inside(<SessionsRoute />)} />
      <Route
        path="/Organizations/Politicas"
        element={inside(<OrgRoute render={(id) => <PoliciesPage key={id} orgId={id} />} />)}
      />
      <Route
        path="/Organizations/RecuperacionAcceso"
        element={inside(<OrgRoute render={(id) => <RecoverPage key={id} orgId={id} />} />)}
      />
      <Route
        path="/Organizations/Soporte"
        element={
          inside(<OrgRoute render={(id) => <DiagnosticPage key={id} orgId={id} canErrorHub={session.canErrorHub} />} />)
        }
      />
      <Route path="/Organizations/EventosTecnicos" element={<OrgRedirect to="/Organizations/Soporte?id={id}" />} />
      <Route
        path="/Organizations/Uso"
        element={inside(<OrgRoute render={(id) => <OrgUsagePage key={id} orgId={id} />} />)}
      />
      <Route
        path="/Organizations/Trazas"
        element={<OrgRedirect to="/Organizations/Uso?id={id}" keep={["period", "mode", "type"]} />}
      />
      <Route
        path="/Organizations/Auditoria"
        element={inside(<OrgRoute render={(id) => <AuditPage key={id} orgId={id} />} />)}
      />
      {/* Empleados IA y conocimiento (R5, especificación §4). */}
      <Route
        path="/Organizations/Empleados"
        element={inside(<OrgRoute render={(id) => <EmployeesPage key={id} orgId={id} />} />)}
      />
      <Route path="/Organizations/Empleado" element={inside(<EmployeeRoute />)} />
      <Route path="/Organizations/Capacidades" element={inside(<EmployeeRoute tab="capacidades" />)} />
      <Route path="/Organizations/EmpleadoConocimiento" element={inside(<EmployeeRoute tab="conocimiento" />)} />
      <Route
        path="/Organizations/Conocimiento"
        element={inside(<OrgRoute render={(id) => <KnowledgePage key={id} orgId={id} />} />)}
      />
      {["/Organizations/ConocimientoDetalle", "/Organizations/ConocimientoBase"].map((path) => (
        <Route key={path} path={path} element={inside(<BaseRoute />)} />
      ))}
      <Route path="/Organizations/ConocimientoFuente" element={inside(<SourceRoute />)} />
      <Route path="/Organizations/ConocimientoFuenteNueva" element={inside(<SourceNewRoute />)} />
      <Route
        path="/Organizations/ConocimientoTerminos"
        element={inside(<OrgRoute render={(id) => <TermsPage key={id} orgId={id} />} />)}
      />
      <Route path="/Organizations/Aprobaciones" element={inside(<ApprovalsRoute />)} />
      <Route
        path="/Organizations/Aprobaciones/Politicas"
        element={inside(<OrgRoute render={(id) => <ApprovalPoliciesPage key={id} orgId={id} />} />)}
      />
      <Route path="/Organizations/Aprobacion" element={inside(<ApprovalRoute />)} />
      {/* Mensajería (R6, especificación §4): Cliente del Blazor → la conversación simulada de ese cliente. */}
      <Route
        path="/Organizations/Bandejas"
        element={inside(<OrgRoute render={(id) => <InboxesPage key={id} orgId={id} />} />)}
      />
      <Route
        path="/Organizations/Canales"
        element={inside(<OrgRoute render={(id) => <ChannelsPage key={id} orgId={id} />} />)}
      />
      <Route
        path="/Organizations/Reglas"
        element={inside(<OrgRoute render={(id) => <RulesPage key={id} orgId={id} />} />)}
      />
      <Route path="/Organizations/Conversaciones" element={inside(<ConversationsRoute />)} />
      <Route path="/Organizations/Conversacion" element={inside(<ConversationRoute />)} />
      <Route
        path="/Organizations/Simulator"
        element={inside(<OrgRoute render={(id) => <SimulatorPage key={id} orgId={id} />} />)}
      />
      <Route path="/Organizations/Chat" element={inside(<SimChatRoute />)} />
      <Route
        path="/Organizations/Client"
        element={<OrgRedirect to="/Organizations/Chat?id={id}" keep={["contactId"]} />}
      />
      <Route path="/Ai" element={<Navigate replace to="/Ai/Config/Index" />} />
      <Route path="/Ai/Config/Index" element={inside(<OverviewPage />)} />
      {/* IA (R2, especificación §4): Resumen · Proveedores · Modelos · Rutas · Uso y costos, y las rutas viejas que se
          funden redirigen a su destino nuevo. */}
      <Route path="/Ai/Config/Conectar" element={<LegacyAiRedirect kind="connect" />} />
      <Route path="/Ai/Providers/Index" element={inside(<ProvidersPage />)} />
      <Route path="/Ai/Providers/:code" element={inside(<ProviderRoute />)} />
      <Route path="/Ai/Conexiones/Index" element={<Navigate replace to="/Ai/Providers/Index" />} />
      {["/Ai/Conexiones/Detail", "/Ai/Conexiones/Detail/:id", "/Ai/Providers/Connection/:id"].map((path) => (
        <Route key={path} path={path} element={inside(<ConnectionRedirect />)} />
      ))}
      <Route path="/Ai/Conexiones/AgregarClave" element={inside(<ConnectionRedirect query="connectionId" addKey />)} />
      <Route path="/Ai/Modelos/Index" element={inside(<ModelsPage />)} />
      {["/Ai/Modelos/Detail", "/Ai/Modelos/Detail/:id"].map((path) => (
        <Route
          key={path}
          path={path}
          element={inside(<IdRoute fallback="/Ai/Modelos/Index" render={(id) => <ModelPage key={id} id={id} />} />)}
        />
      ))}
      <Route path="/Ai/Rutas" element={inside(<RoutesPage />)} />
      {["/Ai/Routing/Index", "/Ai/Asignacion/Index", "/Ai/Asignacion/Cambiar", "/Ai/UsoModelos/Index"].map((path) => (
        <Route key={path} path={path} element={<LegacyAiRedirect kind="routes" />} />
      ))}
      <Route path="/Ai/Avanzado/Index" element={<Navigate replace to="/Ai/Config/Index" />} />
      <Route path="/Ai/Avanzado/Manual" element={inside(<ManualModelPage />)} />
      <Route path="/Ai/Avanzado/Personalizado" element={inside(<CustomProviderPage />)} />
      <Route path="/Ai/Uso/Index" element={inside(<UsagePage />)} />
      <Route path="/Ai/Uso/Ejecuciones" element={inside(<ExecutionsPage />)} />
      {["/Ai/Uso/Detail", "/Ai/Uso/Detail/:id"].map((path) => (
        <Route
          key={path}
          path={path}
          element={inside(
            <IdRoute fallback="/Ai/Uso/Ejecuciones" render={(id) => <UsageDetailPage key={id} id={id} />} />,
          )}
        />
      ))}
      {/* R7 · G5: todas las rutas del Platform Admin actual ya están aquí (§4); lo demás no existe (C40, sin salir al Blazor). */}
      <Route path="*" element={inside(<PageNotFound />)} />
    </Routes>
  );
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [texts, setTexts] = useState<Record<string, string> | null>(null);
  const [failed, setFailed] = useState(false);
  const [locale, setLocale] = useState<Locale>(() => localeFromCookie());
  const [started] = useState(locale);
  useEffect(() => {
    Promise.all([loadSession(), loadTexts(started)])
      .then(([current, values]) => {
        setSession(current);
        setTexts(values);
      })
      .catch(() => setFailed(true));
  }, [started]);
  const value = useMemo<Texts>(() => ({ L: (key) => texts?.[key] || missingText(key), locale }), [texts, locale]);
  // R7 · Mi cuenta: el nombre nuevo llega a la cabecera y las preferencias se aplican sin recargar.
  const refreshSession = useCallback(async () => {
    const current = await loadSession().catch(() => null);
    if (current) setSession(current);
  }, []);
  const applyPreferences = useCallback<AppActions["applyPreferences"]>(async (nextLocale, theme) => {
    paintTheme(theme);
    document.documentElement.setAttribute("lang", nextLocale === "en-US" ? "en" : "es");
    const values = await loadTexts(nextLocale).catch(() => null);
    if (values && Object.keys(values).length > 0) {
      setTexts(values);
      setLocale(nextLocale);
      return ((key) => values[key] || missingText(key)) as Label;
    }
    return value.L;
  }, [value]);
  const actions = useMemo<AppActions>(() => ({ refreshSession, applyPreferences }), [refreshSession, applyPreferences]);
  if (failed)
    return (
      <main className="npa-fatal" role="alert">
        {/* Sin servidor no hay textos que pedir: el único mensaje fijo del producto, en los dos idiomas. */}
        <p>Bheli Admin no está disponible. / Bheli Admin is unavailable.</p>
        <button type="button" className="tm-btn secondary" onClick={() => window.location.reload()}>
          Reintentar / Retry
        </button>
      </main>
    );
  if (!session || !texts) return null;
  return (
    <TextsContext.Provider value={value}>
      <AppContext.Provider value={actions}>
        <BrowserRouter>
          <Screens session={session} />
        </BrowserRouter>
      </AppContext.Provider>
    </TextsContext.Provider>
  );
}
